import { memo, useEffect, useState } from "react";

import type { NavbarClip } from "@/lib/events/registry";

/**
 * Công thức tạo clip banner — áp cho MỌI lễ, không riêng 2/9.
 *
 * Nguồn là file `quockhanh.gif` (2457×832, 85MB) — đã nén lại thành:
 *   - `navbar-quockhanh.mp4`  ~304KB, H.264 yuv420p, 1152×320, 25fps, 7,68s
 *   - `navbar-quockhanh.webp` ~59KB, khung đầu tiên, dùng làm poster / bản tĩnh
 *
 * Lệnh nén (giữ lại để nén artwork sau này cho khớp):
 *   ffmpeg -i quockhanh.gif -vf "fps=25,crop=2457:682:0:150,scale=1152:-2,\
 *     format=yuv420p" -c:v libx264 -crf 32 -preset veryslow -movflags +faststart \
 *     -an navbar-quockhanh.mp4
 *
 * ⚠️ `fps=25` KHÔNG được bỏ: gif gốc có delay thay đổi (30/40/60/70ms), để ffmpeg tự
 * chọn 30fps cố định là 184 khung phát trong 6,1s thay vì 7,66s — clip nhanh hơn ~25%
 * và mất mấy nhịp ngưng có chủ ý, không có lỗi nào báo.
 *
 * `crop=2457:682:0:150` = chỉ cắt ~1/5 phía TRÊN (mây trời, phần không nhận ra là gì
 * ở cỡ 92px), giữ NGUYÊN khung theo chiều ngang. Nhờ cắt trên mà tỉ lệ từ 2,95:1 lên
 * 3,60:1, đủ ngang để dải trải kín bề rộng thanh mà `cover` chỉ cắt thêm ~12% chiều
 * cao. Chừa ~6px phía trên ngọn số "2·9" để nó không dính mép.
 *
 * ⚠️ Đã có một bản cắt thêm 2/5 bên TRÁI (`crop=1497:428:960:110`) để đẩy cụm "QUỐC
 * KHÁNH VIỆT NAM" ra khỏi vùng ô số dư. Nó giải quyết được việc che chữ nhưng mất
 * lăng Bác, và bản đó đã bị bỏ. Đừng cắt lại theo hướng đó; muốn chữ hiện trọn thì
 * thu cụm hộp thư + ô số dư lại.
 *
 * 1152px bề rộng là vừa đủ: dải bị `max-width: 24rem` chặn ở 384px CSS, tức còn 3x
 * pixel cho màn DPR 3.
 */

export type EventNavbarProps = {
  clip: NavbarClip;
  /** Bỏ hẳn thẻ video, chỉ giữ khung hình tĩnh (giảm chuyển động / máy yếu). */
  staticOnly?: boolean;
};

/**
 * EventNavbar — banner của dịp lễ đang bật, làm nền thanh điều hướng trên MOBILE.
 *
 * Banner là artwork ĐỤC, trải từ mép trái thanh sang phải rồi mờ dần (mask) ở khúc
 * đặt hộp thư + ô số dư — nên không dùng `mix-blend-mode`. Bản dải cờ cũ phải blend
 * vì artwork nằm trên một nền một-màu cần khoá đi, và vì thế mới cần HAI file video
 * (nền trắng + `multiply` cho theme sáng, nền đen + `screen` cho theme tối). Artwork
 * này không có nền cần khoá nên chỉ còn MỘT file, dùng chung cho cả hai theme — đừng
 * thêm lại nhánh theme nếu không đổi artwork. Hình học của dải (neo, cách vừa khung,
 * mask mờ dần) nằm hết trong `styles.css`.
 *
 * === Vì sao motion khởi động trễ ===
 * Bật video ngay lúc mount làm cả trang khựng một nhịp mỗi lần tải lại: header đang
 * có `backdrop-filter`, cộng thêm một lớp video nữa là buộc trình duyệt hợp thành lại
 * cả vùng đó — đúng lúc main thread còn bận dựng trang. Khung tĩnh hiện trước, motion
 * vào sau khi máy rảnh; người dùng gần như không thấy khác.
 */
export const EventNavbar = memo(function EventNavbar({
  clip,
  staticOnly = false,
}: EventNavbarProps) {
  const [motion, setMotion] = useState(false);

  useEffect(() => {
    if (staticOnly) {
      setMotion(false);
      return;
    }
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(() => setMotion(true), { timeout: 2000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(() => setMotion(true), 1200);
    return () => window.clearTimeout(id);
  }, [staticOnly]);

  return (
    <div className="event-navbar" aria-hidden="true">
      {/* 🔴 ĐÚNG MỘT lớp tại một thời điểm — khung tĩnh và video dùng chung class nên
          để cả hai cùng nằm đó là bản tĩnh đứng im lộ ra sau bản đang chạy.
          Khoảnh khắc chuyển không hở: thẻ video mang `poster` đúng bức ảnh này. */}
      {motion ? (
        <video
          className="event-navbar-frame"
          src={clip.video}
          poster={clip.poster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          tabIndex={-1}
        />
      ) : (
        <div className="event-navbar-frame" style={{ backgroundImage: `url('${clip.poster}')` }} />
      )}
    </div>
  );
});
